import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Button from "@/common/elements/Button";
import Modal from "@/common/elements/Modal";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormContainer } from "../InmateDetails/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { Checkbox, Form } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import {
  getDayjsObject,
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";

export const MODE = { NEW: "NEW", EDIT: "EDIT" };

const ChargesInformationModal = ({
  show,
  onHide,
  mode = MODE.NEW,
  selectChargeInfo,
  forwardSave,
  startingPoint = "",
}) => {
  const [form] = Form.useForm();
  const [chargeInfo, setChargeInfo] = useState(null);
  const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);

  // -----------------------------------------------------------------------
  // Sync selectChargeInfo → local state (with dayjs conversions)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (isNonEmptyVariable(selectChargeInfo)) {
      setChargeInfo({
        ...selectChargeInfo,
        offenseDate: selectChargeInfo?.offenseDate
          ? dayjs(selectChargeInfo.offenseDate)
          : null,
        clearanceDateTime: getDayjsObject(
          selectChargeInfo?.clearanceDate,
          selectChargeInfo?.clearanceTime
        ),
        scheduledReleaseDateTime: getDayjsObject(
          selectChargeInfo?.scheduledReleaseDate,
          selectChargeInfo?.scheduledReleaseTime
        ),
      });
    } else {
      setChargeInfo(null);
    }
  }, [selectChargeInfo]);

  // Sync local state → antd form
  useEffect(() => {
    if (isNonEmptyVariable(chargeInfo)) {
      form.setFieldsValue(chargeInfo);
    } else {
      form.resetFields();
    }
  }, [chargeInfo]);

  // -----------------------------------------------------------------------
  // Save — split combined datetime fields back into date + time
  // -----------------------------------------------------------------------
  const onChargesInformationSaveClick = () => {
    const values = form.getFieldsValue();

    if (values.offenseDate?.format) {
      values.offenseDate = values.offenseDate.format("MM/DD/YYYY");
    }

    if (values.scheduledReleaseDateTime?.format) {
      const dt = values.scheduledReleaseDateTime;
      values.scheduledReleaseDate = dt.format("MM/DD/YYYY");
      values.scheduledReleaseTime = dt.format("HH:mm");
      delete values.scheduledReleaseDateTime;
    }

    if (values.clearanceDateTime?.format) {
      const dt = values.clearanceDateTime;
      values.clearanceDate = dt.format("MM/DD/YYYY");
      values.clearanceTime = dt.format("HH:mm");
      delete values.clearanceDateTime;
    }

    forwardSave(values, selectChargeInfo?.id);
    onHide();
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Modal
      show={show}
      onHide={onHide}
      designPattern="ant"
      header="Detention Charge Details"
      width="50%"
      footerActionsNeed
      footerActions={[
        <Button
          key="save"
          type="primary"
          onClick={onChargesInformationSaveClick}
          disabled={!bookingsFieldsEnabled}
        >
          Save
        </Button>,
        <Button key="cancel" onClick={onHide}>
          Cancel
        </Button>,
      ]}
    >
      <FieldSecurityProvider
        entity="DetentionsCharges"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <FormContainer>
          <Form form={form} initialValues={chargeInfo}>

            {/* ── Charge Information ─────────────────────────────────── */}
            <StyledSectionHeading>
              <span>Charge Information</span>
            </StyledSectionHeading>

            <StyledGridRow justify="end">
              <StyledGridCol>
                {/* Sealed — checkbox with field-level security */}
                <SecureFormItem
                  fieldName="sealed"
                  formItemProps={{
                    name: "sealed",
                    label: "Sealed?",
                    layout: "horizontal",
                    labelPostion: "label-right",
                    valuePropName: "checked",
                  }}
                >
                  <Checkbox />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

            <StyledGridRow gutter={16}>

              {/* Charge */}
              <StyledGridCol lg={12}>
                <SecureFormItem
                  fieldName="chargeDescriptionID"
                  formItemProps={{ name: "chargeDescriptionID", label: "Charge" }}
                >
                  <Select
                    options={getOptionsFromRefList("Charges", false, false, true)}
                    placeholder="Charge"
                    showSearch={false}
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>

              {/* Code */}
              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="ucrCode"
                  formItemProps={{ name: "ucrCode", label: "Code" }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>

              {/* Level */}
              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="chargeLevelID"
                  formItemProps={{ name: "chargeLevelID", label: "Level" }}
                >
                  <Select
                    options={getOptionsFromRefList("Charge_Level", false, false, true)}
                    placeholder="Level"
                    showSearch={false}
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>

              {/* Offense Date */}
              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="offenseDate"
                  formItemProps={{ name: "offenseDate", label: "Offense Date" }}
                >
                  <CommonDatePicker
                    placeholder="MM/DD/YYYY"
                    format="MM/DD/YYYY"
                    showTime={false}
                  />
                </SecureFormItem>
              </StyledGridCol>

              {/* Scheduled Release */}
              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="scheduledReleaseDateTime"
                  formItemProps={{
                    name: "scheduledReleaseDateTime",
                    label: "Scheduled Release",
                  }}
                >
                  <CommonDatePicker
                    placeholder="MM/DD/YYYY HH:mm"
                    showTime
                  />
                </SecureFormItem>
              </StyledGridCol>

            </StyledGridRow>

            {/* ── Clearance Information ───────────────────────────────── */}
            <StyledSectionHeading>
              <span>Clearance Information</span>
            </StyledSectionHeading>

            <StyledGridRow gutter={16}>

              {/* Date/Time */}
              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="clearanceDateTime"
                  formItemProps={{ name: "clearanceDateTime", label: "Date/Time" }}
                >
                  <CommonDatePicker />
                </SecureFormItem>
              </StyledGridCol>

              {/* Reason */}
              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="clearanceReasonID"
                  formItemProps={{ name: "clearanceReasonID", label: "Reason" }}
                >
                  <Select
                    options={getOptionsFromRefList("ChargeClearance", false, false, true)}
                  />
                </SecureFormItem>
              </StyledGridCol>

            </StyledGridRow>

          </Form>
        </FormContainer>
      </FieldSecurityProvider>
    </Modal>
  );
};

export default ChargesInformationModal;